/**
 * Shared Partner banking validation (onboarding + profile correction).
 * Keep in sync with backend/banking_validation.py
 */

export const ACCOUNT_NUMBER_ERROR = 'Account number must be 9 to 18 digits';
export const BRANCH_NAME_MAX_LEN = 200;

export const UPI_ID_PATTERN = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
export const INDIAN_MOBILE_PATTERN = /^[6-9]\d{9}$/;
export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export const UPI_ID_REQUIRED_ERROR = 'UPI ID is required';
export const UPI_ID_FORMAT_ERROR = 'Invalid UPI format (e.g., name@upi)';
export const LINKED_MOBILE_REQUIRED_ERROR = 'Linked mobile number is required';
export const LINKED_MOBILE_FORMAT_ERROR =
  'Enter a valid 10-digit Indian mobile number (starts with 6-9)';
export const IFSC_REQUIRED_ERROR = 'IFSC code is required';
export const IFSC_FORMAT_ERROR = 'Invalid IFSC format (e.g., HDFC0001234)';

export function sanitizeAccountNumberInput(raw) {
  return String(raw ?? '').replace(/\D/g, '').slice(0, 18);
}

export function validateAccountNumber(value) {
  if (!value?.trim()) return 'Account number is required';
  const trimmed = value.trim();
  if (!/^\d{9,18}$/.test(trimmed)) return ACCOUNT_NUMBER_ERROR;
  return '';
}

export function validateBranchName(value, { required = false } = {}) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return required ? 'Branch name is required' : '';
  }
  if (trimmed.length > BRANCH_NAME_MAX_LEN) {
    return `Maximum ${BRANCH_NAME_MAX_LEN} characters allowed`;
  }
  return '';
}

/**
 * Strip surrounding whitespace and common digit separators only.
 * Does not truncate digits or invent country-code rewriting.
 */
export function normalizeLinkedMobileInput(value) {
  return String(value ?? '')
    .trim()
    .replace(/[\s\-()]/g, '');
}

/**
 * Sanitize keystrokes for the mobile field: keep digits only (no silent length truncate).
 */
export function sanitizeLinkedMobileKeystroke(raw) {
  return String(raw ?? '').replace(/\D/g, '');
}

export function validateLinkedMobile(value, { required = true } = {}) {
  const normalized = normalizeLinkedMobileInput(value);
  if (!normalized) {
    return required ? LINKED_MOBILE_REQUIRED_ERROR : '';
  }
  if (!/^\d+$/.test(normalized)) {
    return LINKED_MOBILE_FORMAT_ERROR;
  }
  if (!INDIAN_MOBILE_PATTERN.test(normalized)) {
    return LINKED_MOBILE_FORMAT_ERROR;
  }
  return '';
}

/** Trim only — reject embedded whitespace rather than removing it silently from handles. */
export function normalizeUpiIdInput(value) {
  return String(value ?? '').trim();
}

/**
 * Soft keystroke filter for UPI (allowed VPA charset). Does not invent provider lists.
 */
export function sanitizeUpiIdKeystroke(raw) {
  return String(raw ?? '').replace(/[^a-zA-Z0-9._@-]/g, '');
}

export function validateUpiId(value, { required = true } = {}) {
  const trimmed = normalizeUpiIdInput(value);
  if (!trimmed) {
    return required ? UPI_ID_REQUIRED_ERROR : '';
  }
  if (/\s/.test(trimmed)) {
    return UPI_ID_FORMAT_ERROR;
  }
  if (!UPI_ID_PATTERN.test(trimmed)) {
    return UPI_ID_FORMAT_ERROR;
  }
  return '';
}

export function normalizeIfscInput(value) {
  return String(value ?? '').trim().toUpperCase();
}

export function validateIfscCode(value, { required = true } = {}) {
  const trimmed = normalizeIfscInput(value);
  if (!trimmed) {
    return required ? IFSC_REQUIRED_ERROR : '';
  }
  if (!IFSC_PATTERN.test(trimmed)) {
    return IFSC_FORMAT_ERROR;
  }
  return '';
}
